import {useFeatureIsOn} from "@growthbook/growthbook-react";
import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";
import {useState} from "react";
import {DndProvider} from "react-dnd";
import {HTML5Backend} from "react-dnd-html5-backend";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {TextField} from "@khanacademy/wonder-blocks-form";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {LabeledField} from "@khanacademy/wonder-blocks-labeled-field";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    LabelMedium,
    LabelSmall,
    LabelXSmall,
} from "@khanacademy/wonder-blocks-typography";
import {ImageSource, type ImageInput} from "@repo/guide-types";

import {DragAndDropFileTarget} from "../drag-drop-file-target/drag-drop-file-target.tsx";

import {ALLOWED_FILE_TYPES} from "./constants.ts";
import {ImageAttach} from "./image-attach.tsx";
import {ImagePreview} from "./image-preview.tsx";

import imageIcon from "@phosphor-icons/core/regular/image.svg";
import plusCircleIcon from "@phosphor-icons/core/regular/plus-circle.svg";
import trashIcon from "@phosphor-icons/core/regular/trash-simple.svg";

interface Props {
    onCloseModal: () => void;
    onAttachImages?: (images: ImageInput[]) => void;
}

const PREVIEW_HEIGHT = 296;
const PREVIEW_WIDTH = 508;
// 2px padding on each side.  Without this, the image overlaps the dotted
// border, which is weird.
const PADDING_TO_REVEAL_BORDER = 4;
// An arbitrary height that works for the content in both shared tabs.
const MODAL_CONTENT_HEIGHT = 422;
const DND_BORDER = 2;
// An arbitrary height that prevents a vertical scrollbar on initial load.
const MODAL_CONTAINER_HEIGHT = 640;

/**
 * A modal that allows the user to attach an image from their device or
 * provide a URL to an image.
 *
 * Currently allows user to submit an image one of three ways:
 * 1. Drag and drop an image file onto the modal.
 * 2. Click the "Choose file" button and select an image file from the file picker.
 * 3. Click the "Use a URL" tab and paste a URL to an image.
 */
export function ImageAttachModal({
    onCloseModal,
    onAttachImages,
}: Props): React.ReactElement {
    const [overTarget, setOverTarget] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const allowUrlsFlag = useFeatureIsOn("images-in-images-out-urls");
    const [image, setImage] = useState<ImageInput>();
    const [imageURL, setImageURL] = useState<string>("");
    const [pendingImageURL, setPendingImageURL] = useState<string>("");
    const [draggedFile, setDraggedFile] = useState<File>();
    const [selectedFilename, setSelectedFilename] = useState<string | null>(
        null,
    );

    const clearImage = () => {
        setImage(undefined);
        setDraggedFile(undefined);
        setSelectedFilename(null);
    };

    const clearImageURL = () => {
        setImageURL("");
        setPendingImageURL("");
    };

    const handleSetImage = (dataURL: string, source: ImageInput["source"]) => {
        setImage({
            imageURL: dataURL,
            moderated: false,
            source,
        });
    };

    const handleSetImageURL = () => {
        setError(null);
        clearImage();
        handleConvertImageURLToDataURL(pendingImageURL);
    };

    const handleDraggedFile = (file) => {
        clearImageURL();
        setDraggedFile(file);
    };

    // A simple check to see if the data URL appears to be one of the allowed types.
    const validateDataURL = (dataURL: string): boolean => {
        const fileType = dataURL.split(";")[0].split(":")[1];
        return ALLOWED_FILE_TYPES.includes(fileType);
    };

    const handleConvertImageURLToDataURL = async (url) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const reader = new FileReader();
            reader.onerror = (error) => {
                handleClearImage();
            };
            reader.onload = () => {
                const maybeImageResult = reader.result as string;
                if (!validateDataURL(maybeImageResult)) {
                    setError(
                        t`File type not supported.  Please use a supported format (PNG or JPG)`,
                    );
                    handleClearImage();
                    return;
                }
                setImageURL(url);
                handleSetImage(maybeImageResult, ImageSource.ProvidedByURL);
            };
            reader.readAsDataURL(blob);
        } catch (error) {
            setError(
                t`We couldn't fetch that image URL.  Please try again, or try a different URL`,
            );
            handleClearImage();
        }
    };

    const handleConfirmAttachment = () => {
        if (!image) {
            throw new Error("No image to attach");
        }
        onAttachImages?.([image]);
        onCloseModal();
    };

    // Removes the selected image so the user can try a different image
    const handleClearImage = () => {
        clearImage();
        clearImageURL();
    };

    const handleCancel = () => {
        onCloseModal();
    };

    const handleOverTarget = (v: boolean) => {
        setOverTarget(v);
    };

    return (
        <OnePaneDialog
            style={styles.modalContainer}
            content={
                <View style={styles.modalContent}>
                    <LabelSmall>
                        <Trans>
                            Images should not contain any personal information
                            such as names, addresses, phone numbers, or
                            sensitive data.
                        </Trans>
                    </LabelSmall>
                    <Strut size={spacing.medium_16} />
                    <View style={styles.dndContainer}>
                        {/* @ts-expect-error - TS2322 - Type '{ children: Element; backend: BackendFactory; }' is not assignable to type 'IntrinsicAttributes & DndProviderProps<unknown, unknown>'. */}
                        <DndProvider backend={HTML5Backend}>
                            <DragAndDropFileTarget
                                onDrop={handleDraggedFile}
                                onOver={handleOverTarget}
                            >
                                {image ? (
                                    <View style={styles.previewContainer}>
                                        <ImagePreview
                                            displayHeight={PREVIEW_HEIGHT}
                                            displayWidth={PREVIEW_WIDTH}
                                            image={image.imageURL}
                                        />
                                    </View>
                                ) : (
                                    <View
                                        style={[
                                            styles.previewContainer,
                                            styles.dndTarget,
                                            overTarget && styles.overTarget,
                                        ]}
                                    >
                                        <PhosphorIcon
                                            icon={plusCircleIcon}
                                            size="medium"
                                        />
                                        <View style={styles.dndText}>
                                            <Trans>Drag and Drop here or</Trans>
                                            <ImageAttach
                                                image={image ?? null}
                                                onImageAttach={handleSetImage}
                                                fileDragged={draggedFile}
                                                setSelectedFilename={
                                                    setSelectedFilename
                                                }
                                            />
                                        </View>
                                    </View>
                                )}
                            </DragAndDropFileTarget>
                        </DndProvider>
                    </View>

                    <View style={styles.supportedFormats}>
                        <LabelXSmall>{t`Supported formats: PNG, JPEG`}</LabelXSmall>
                        <LabelXSmall>{t`Maximum size: 20 MB`}</LabelXSmall>
                    </View>

                    {image && (
                        <View style={styles.imageFilenameContainer}>
                            <View style={styles.imageFilenameRow}>
                                <PhosphorIcon icon={imageIcon} size="medium" />
                                <LabelMedium
                                    style={styles.imageFilenameOverflow}
                                >
                                    {selectedFilename}
                                    {imageURL}
                                </LabelMedium>
                            </View>
                            <IconButton
                                icon={trashIcon}
                                kind="tertiary"
                                size="small"
                                onClick={handleClearImage}
                                aria-label={t`Clear image`}
                                testId="image-attach-clear-button"
                            />
                        </View>
                    )}

                    {allowUrlsFlag && (
                        <>
                            <View style={styles.dividerContainer}>
                                <View style={styles.divider} />
                                <LabelXSmall>{t`OR`}</LabelXSmall>
                                <View style={styles.divider} />
                            </View>
                            <View style={styles.urlContainer}>
                                <LabeledField
                                    field={
                                        <TextField
                                            placeholder="https://khanacademy.org/algebra1/..."
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    handleSetImageURL();
                                                }
                                            }}
                                            onBlur={handleSetImageURL}
                                            value={pendingImageURL}
                                            onChange={setPendingImageURL}
                                        />
                                    }
                                    errorMessage={error}
                                    styles={{root: styles.urlInput}}
                                    label={t`From URL`}
                                />
                            </View>
                        </>
                    )}
                </View>
            }
            footer={
                <View style={styles.footer}>
                    <Button
                        kind="tertiary"
                        onClick={handleCancel}
                        aria-label={t`Cancel image attach`}
                    >
                        {t`Cancel`}
                    </Button>
                    <Button
                        kind="primary"
                        disabled={!image}
                        onClick={handleConfirmAttachment}
                    >
                        {t`Attach image`}
                    </Button>
                </View>
            }
            title={t`Attach image`}
        />
    );
}

const styles = StyleSheet.create({
    urlInput: {
        width: "100%",
    },
    modalContainer: {
        minBlockSize: MODAL_CONTAINER_HEIGHT,
    },
    modalContent: {
        alignItems: "center",
        justifyContent: "flex-start",
        height: MODAL_CONTENT_HEIGHT,
    },
    dividerContainer: {
        flexDirection: "row",
        width: "100%",
        gap: spacing.large_24,
        alignItems: "center",
        marginBlockStart: spacing.medium_16,
        marginBlockEnd: spacing.xSmall_8,
    },
    divider: {
        height: 1,
        width: "100%",
        backgroundColor: color.offBlack50,
        margin: "0 auto",
    },
    imageFilenameContainer: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        margin: spacing.small_12,
        backgroundColor: "#F7F8FA",
        padding: spacing.medium_16,
        borderRadius: 8,
        width: "100%",
    },
    imageFilenameRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.medium_16,
    },
    imageFilenameOverflow: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    footer: {
        width: "100%",
        flexDirection: "row",
        gap: spacing.small_12,
        justifyContent: "right",
    },
    supportedFormats: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        marginBlockStart: spacing.medium_16,
    },
    dndText: {
        flexDirection: "row",
        gap: spacing.xxxSmall_4,
        alignItems: "center",
    },
    dndContainer: {
        // Keep the content area from reflowing by aligning
        // the height of the DND target with the height of the image preview.
        width: "100%",
        minInlineSize: PREVIEW_WIDTH + PADDING_TO_REVEAL_BORDER,
        height: PREVIEW_HEIGHT + PADDING_TO_REVEAL_BORDER,
        minBlockSize: PREVIEW_HEIGHT + PADDING_TO_REVEAL_BORDER,
    },
    previewContainer: {
        fontSize: 16,
        // Keep the content area from reflowing by aligning
        // the height of the preview container with the height of the image preview.
        width: "100%",
        minInlineSize: PREVIEW_WIDTH + PADDING_TO_REVEAL_BORDER,
        height: PREVIEW_HEIGHT + PADDING_TO_REVEAL_BORDER,
        minBlockSize: PREVIEW_HEIGHT + PADDING_TO_REVEAL_BORDER,
        alignItems: "center",
        justifyContent: "center",

        border: `${DND_BORDER}px dashed ${color.offBlack16}`,
        borderRadius: 8,
    },
    urlContainer: {
        width: "100%",
    },
    dndTarget: {
        backgroundColor: "#F3F4F5",
    },
    overTarget: {
        backgroundColor: color.fadedBlue16,
        border: `${DND_BORDER}px dashed ${color.blue}`,
    },
});
